"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Plus } from "lucide-react"
import { useAuth } from "@/hooks/use-auth"
import type { ProductOption } from "@/types/shopify"
import { MIN_SEARCH_LENGTH, ProductSearchPanel } from "./product-search-panel"
import { useTranslations } from "next-intl"


const DEBOUNCE_MS = 300

interface ProductSearchContainerProps {
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
  minimumOrderTotal?: number
  /** Product IDs already in the list (for duplicate prevention) */
  excludeProductIds: Set<number>
  onSelect: (products: ProductOption[]) => void
}

export function ProductSearchContainer({
  integrationId,
  monthStartUtc,
  monthEndUtc,
  minimumOrderTotal = 0,
  excludeProductIds,
  onSelect,
}: ProductSearchContainerProps) {
  const tUi = useTranslations("Shopify.ShopifyEconomicsProductSearchContainer")

  const { fetchWithAuth } = useAuth()
  const fetchRef = useRef(fetchWithAuth)
  fetchRef.current = fetchWithAuth

  const [popoverOpen, setPopoverOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [results, setResults] = useState<ProductOption[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [checked, setChecked] = useState<Map<number, ProductOption>>(new Map())

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const popoverInputRef = useRef<HTMLInputElement>(null)
  const dialogInputRef = useRef<HTMLInputElement>(null)

  /** Abort controller for the currently in-flight search; always aborted on resetAll / new query. */
  const searchAbortRef = useRef<AbortController | null>(null)
  /** Monotonic id — late-resolving responses whose id != this are dropped. */
  const searchRequestIdRef = useRef(0)

  /**
   * When true, the next popover close must NOT reset state — we're transitioning
   * into the dialog and want to carry the search + selection across.
   */
  const isExpandingRef = useRef(false)

  const cancelInflightSearch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    if (searchAbortRef.current) {
      searchAbortRef.current.abort()
      searchAbortRef.current = null
    }
    searchRequestIdRef.current += 1
  }, [])

  const resetAll = useCallback(() => {
    cancelInflightSearch()
    setSearch("")
    setResults([])
    setChecked(new Map())
    setIsSearching(false)
  }, [cancelInflightSearch])

  useEffect(() => {
    // Abort any previous request + bump the id so its late response is ignored.
    if (searchAbortRef.current) searchAbortRef.current.abort()
    const controller = new AbortController()
    searchAbortRef.current = controller
    const requestId = ++searchRequestIdRef.current

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (search.length < MIN_SEARCH_LENGTH || !integrationId) {
      setResults([])
      setIsSearching(false)
      return () => controller.abort()
    }

    setIsSearching(true)
    debounceRef.current = setTimeout(() => {
      void (async () => {
        try {
          const params = new URLSearchParams({
            integration_id: integrationId,
            search,
            month_start_utc: monthStartUtc,
            month_end_utc: monthEndUtc,
            minimum_order_total: String(minimumOrderTotal),
          })
          const res = await fetchRef.current(
            `/api/integrations/shopify/products?${params}`,
            { signal: controller.signal },
          )
          if (controller.signal.aborted || requestId !== searchRequestIdRef.current) return
          if (res.ok) {
            const data = await res.json()
            if (controller.signal.aborted || requestId !== searchRequestIdRef.current) return
            setResults(data.products ?? [])
          }
        } catch (err) {
          if (controller.signal.aborted) return
          if ((err as { name?: string } | null)?.name === "AbortError") return
          setResults([])
        } finally {
          if (!controller.signal.aborted && requestId === searchRequestIdRef.current) {
            setIsSearching(false)
          }
        }
      })()
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      controller.abort()
    }
  }, [search, integrationId, monthStartUtc, monthEndUtc, minimumOrderTotal])

  const handleToggle = useCallback((product: ProductOption) => {
    setChecked((prev) => {
      const next = new Map(prev)
      if (next.has(product.product_id)) next.delete(product.product_id)
      else next.set(product.product_id, product)
      return next
    })
  }, [])

  const handleSelectAll = useCallback((selectable: ProductOption[]) => {
    setChecked((prev) => {
      if (selectable.length === 0) return prev
      const next = new Map(prev)
      const allAlreadySelected = selectable.every((p) => next.has(p.product_id))
      if (allAlreadySelected) {
        selectable.forEach((p) => next.delete(p.product_id))
      } else {
        selectable.forEach((p) => next.set(p.product_id, p))
      }
      return next
    })
  }, [])

  const handleConfirm = useCallback(() => {
    if (checked.size === 0) return
    onSelect([...checked.values()])
    resetAll()
    setPopoverOpen(false)
    setDialogOpen(false)
  }, [checked, onSelect, resetAll])

  const handleRequestExpand = useCallback(() => {
    // Mark the transition BEFORE triggering the popover close handler so its
    // onOpenChange can skip the reset branch.
    isExpandingRef.current = true
    setPopoverOpen(false)
    setDialogOpen(true)
    // Radix fires onOpenChange synchronously inside setState; clear on the next
    // microtask so any follow-up close still resets state normally.
    queueMicrotask(() => { isExpandingRef.current = false })
  }, [])

  const handlePopoverOpenChange = useCallback((next: boolean) => {
    if (next) {
      setPopoverOpen(true)
      setTimeout(() => popoverInputRef.current?.focus(), 0)
      return
    }
    setPopoverOpen(false)
    if (isExpandingRef.current) return // preserve state for the dialog
    resetAll()
  }, [resetAll])

  const handleDialogOpenChange = useCallback((next: boolean) => {
    if (next) {
      setDialogOpen(true)
      setTimeout(() => dialogInputRef.current?.focus(), 0)
      return
    }
    setDialogOpen(false)
    resetAll()
  }, [resetAll])

  // Keyboard: "/" focuses the input when the dialog is open (and focus isn't
  // already in a form element).
  useEffect(() => {
    if (!dialogOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "/") return
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return
      e.preventDefault()
      dialogInputRef.current?.focus()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [dialogOpen])

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-1.5" />
            <span><span key={tUi("addProducts")}>{tUi("addProducts")}</span></span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] p-0" align="start">
          <ProductSearchPanel
            ref={popoverInputRef}
            excludeProductIds={excludeProductIds}
            search={search}
            onSearchChange={setSearch}
            results={results}
            isSearching={isSearching}
            checked={checked}
            onToggle={handleToggle}
            onSelectAll={handleSelectAll}
            onConfirm={handleConfirm}
            onRequestExpand={handleRequestExpand}
          />
        </PopoverContent>
      </Popover>

      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-4xl w-full h-[85vh] p-0 flex flex-col gap-0">
          <DialogHeader className="px-4 pt-4 pb-3 border-b shrink-0">
            <DialogTitle><span key={tUi("addProducts2")}>{tUi("addProducts2")}</span></DialogTitle>
            <DialogDescription>
              <span><span key={tUi("searchTheCatalogAndPickMultipleProductsPress")}>{tUi("searchTheCatalogAndPickMultipleProductsPress")}</span></span> <kbd className="text-xs">/</kbd> <span><span key={tUi("toFocusSearch")}>{tUi("toFocusSearch")}</span></span> <kbd className="text-xs"><span key={"Esc"}>{"Esc"}</span></kbd> <span><span key={tUi("toClose")}>{tUi("toClose")}</span></span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            <ProductSearchPanel
              ref={dialogInputRef}
              excludeProductIds={excludeProductIds}
              search={search}
              onSearchChange={setSearch}
              results={results}
              isSearching={isSearching}
              checked={checked}
              onToggle={handleToggle}
              onSelectAll={handleSelectAll}
              onConfirm={handleConfirm}
              dense
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
